import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZES: Record<AvatarSize, string> = {
  xs: 'size-6 text-xs',
  sm: 'size-8 text-xs',
  md: 'size-9 text-sm',
  lg: 'size-12 text-base',
  xl: 'size-24 text-3xl',
  '2xl': 'size-28 text-4xl',
};

/** Lato in pixel di ciascuna misura: fino a 48 px basta la foto da 128, oltre serve quella da 512. */
const PIXELS: Record<AvatarSize, number> = { xs: 24, sm: 32, md: 36, lg: 48, xl: 96, '2xl': 112 };

/**
 * Avatar con la foto del profilo o, in sua assenza, le iniziali su giallo (`bg-bsite-400
 * text-ink`). La foto si mostra quando ci sono sia `userId` sia `avatarVersion`
 * (`/api/users/{id}/avatar?size&v`, research R-04); se non si carica tornano le iniziali. Il tono
 * `neutral` distingue gli altri partecipanti, come nella tavola M·01.
 */
@Component({
  selector: 'bs-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex shrink-0' },
  template: `
    @if (photoUrl(); as src) {
      <img
        [src]="src"
        [alt]="name() ?? ''"
        [attr.title]="name()"
        [class]="photoClasses()"
        decoding="async"
        data-avatar="photo"
        (error)="failedUrl.set(src)"
      />
    } @else {
      <span
        [class]="classes()"
        [attr.title]="name()"
        [attr.role]="name() ? 'img' : null"
        [attr.aria-label]="name()"
        [attr.aria-hidden]="name() ? null : 'true'"
        data-avatar="initials"
      >
        {{ initials() }}
      </span>
    }
  `,
})
export class AvatarComponent {
  readonly initials = input.required<string>();
  /** Nome completo, letto dai lettori di schermo; senza, l'avatar è decorativo. */
  readonly name = input<string | null>(null);
  readonly size = input<AvatarSize>('md');
  readonly tone = input<'brand' | 'neutral'>('brand');
  /** Utente di cui mostrare la foto. */
  readonly userId = input<string | null>(null);
  /** Versione della foto (`avatarVersion`): cambia a ogni nuova foto, quindi la cache non sbaglia. */
  readonly avatarVersion = input<string | null>(null);

  /** Indirizzo dell'ultima foto che non si è caricata: per quella si mostrano le iniziali. */
  protected readonly failedUrl = signal<string | null>(null);

  protected readonly photoUrl = computed(() => {
    const id = this.userId();
    const version = this.avatarVersion();
    if (!id || !version) {
      return null;
    }
    const pixels = PIXELS[this.size()] <= 48 ? 128 : 512;
    const url = `/api/users/${encodeURIComponent(id)}/avatar?size=${pixels}&v=${encodeURIComponent(version)}`;
    return url === this.failedUrl() ? null : url;
  });

  protected readonly photoClasses = computed(
    () => `${SIZES[this.size()]} rounded-full bg-stone-200 object-cover dark:bg-stone-800`,
  );

  protected readonly classes = computed(
    () =>
      `inline-flex items-center justify-center rounded-full font-bold uppercase ${SIZES[this.size()]} ${
        this.tone() === 'brand'
          ? 'bg-bsite-400 text-ink'
          : 'bg-stone-700 text-white dark:bg-stone-300 dark:text-ink'
      }`,
  );
}
